/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import bnd3Service from '../../../services/bnd3.service';
import ChartConst from '../../../constants/chart.constant';
import ConfigApplicationSettings from '../../../config/application.config';
import windbarbService from '../../../services/windbarb.service';
import { is_iphone } from '../../../services/utils/is_iphone';
import { is_ipad } from '../../../services/utils/is_ipad';
import d3 from '../../../globalexports/d3.export';

function windChartService() {
  const content = {};
  content.drawChart = function (
    sourceData,
    settings,
    chartData,
    chartObj,
    chartHeight,
    width,
    topSpace,
    x,
    y1,
    showAbbreviatedYTicks,
  ) {
    let {
      gustHeight,
    } = ChartConst.ChartLabelSpecs.wgs;
    let gustData;
    let defaultWindSpeed;
    // Below logic will make sure that large screen tablets like
    // iPad PRO use same bard config as of for desktop screens
    if (is_iphone()) {
      defaultWindSpeed = ChartConst.windSpeed.mobileLight;
      gustHeight = ChartConst.ChartLabelSpecs.wgs.gustHeightMobile;
    } else if (is_ipad()) {
      defaultWindSpeed = ChartConst.windSpeed.tabletLight;
    } else {
      defaultWindSpeed = ChartConst.windSpeed.light;
    }

    if (!sourceData) {
      return null;
    }
    const data = sourceData.forecast;

    function getWindGustData() {
      _forEach(chartData, (obj) => {
        if (obj.key === ChartConst.ChartLabelSpecs.wgs.key) {
          gustData = obj;
        }
      });
    }

    // Y-Axis left side ChartConst
    const yAxisLeft = d3.axisRight()
      .scale(y1)
      .ticks(ChartConst.GraphConfig.yAxis.ticks)
      .tickFormat((d) => d + (showAbbreviatedYTicks
        ? ''
        : ChartConst.ChartLabelSpecs.wsp.tooltip.unit))
      .tickSizeInner(0) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    // yAxisLeftMinor is for drawing ticks line inside chart
    const yAxisLeftMinor = d3.axisLeft()
      .scale(y1)
      .ticks(ChartConst.GraphConfig.yAxis.ticks)
      .tickFormat('')
      .tickSizeInner(-width) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    // WSP|XWD Line ChartConst
    const line = d3.line()
      .curve(ChartConst.bnd3Curve)
      .defined((d) => d.y !== null)
      .x((d) => x(d.x))
      .y((d) => y1(d.y));
    const area = d3.area()
      .curve(ChartConst.bnd3Curve)
      .defined((d) => d.y !== null)
      .x((d) => x(d.x))
      .y0(chartHeight)
      .y1((d) => y1(d.y));
    // Setting Domain for X-axis
    x.domain(d3.extent(data, (d) => d.forecast_ts));

    function getWindDomainUpperBound() {
      let i = 0;
      let j;
      const allWindValues = [];
      while (i < chartData.length) {
        j = 0;
        while (j < chartData[i].values.length) {
          allWindValues.push(chartData[i].values[j].y);
          j += 1;
        }
        i += 1;
      }
      return Math.max(ChartConst.windDomain.max, Math.max.apply(null, allWindValues));
    }
    if (chartData && chartData.length) {
      // Get windgust data and remove its data from chartData obj after yDomain is set
      getWindGustData();
    }

    // Setting Domain for WSP
    y1.domain([ChartConst.windDomain.min - ChartConst.windDomain.minOffset,
      getWindDomainUpperBound() + ChartConst.windDomain.maxOffset,
    ]);

    // Drawing clear rect to seperate two charts
    chartObj.append('rect')
      .attr('class', 'clear-rect')
      .attr('x', 0)
      .attr('y', chartHeight)
      .attr('width', width)
      .attr('height', topSpace);
    // Temperature Gradient ChartConst
    chartObj.append('linearGradient')
      .attr('id', 'crosswind-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', () => y1(ChartConst.windGradient.xwdMinValue))
      .attr('x2', 0)
      .attr('y2', () => y1(ChartConst.windGradient.xwdMaxValue))
      .selectAll('stop')
      .data(ChartConst.windGradient.gradientConfig)
      .enter()
      .append('stop')
      .attr('offset', (d) => d.offsetXwd)
      .attr('stop-color', (d) => d.color);

    chartObj.append('linearGradient')
      .attr('id', 'windspeed-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', () => y1(ChartConst.windGradient.wspMinValue))
      .attr('x2', 0)
      .attr('y2', () => y1(ChartConst.windGradient.wspMaxValue))
      .selectAll('stop')
      .data(ChartConst.windGradient.gradientConfig)
      .enter()
      .append('stop')
      .attr('offset', (d) => d.offsetWsp)
      .attr('stop-color', (d) => d.color);

    // Temperature Gradient AREA ChartConst
    chartObj.append('linearGradient')
      .attr('id', 'crosswind-gradient-area')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', () => y1(ChartConst.windGradient.xwdMinValue))
      .attr('x2', 0)
      .attr('y2', () => y1(ChartConst.windGradient.xwdMaxValue))
      .selectAll('stop')
      .data(ChartConst.windGradient.gradientConfigArea)
      .enter()
      .append('stop')
      .attr('offset', (d) => d.offsetXwd)
      .attr('stop-color', (d) => d.color);
    // Drawing Y-axis left tick lines
    chartObj.append('g')
      .attr('class', 'y axis y-left y-grid-line-container')
      .call(yAxisLeftMinor);
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-tick-highlighter')
      .call(yAxisLeftMinor);

    // Filter out wgs (wind gust) series. Dont want to
    //  draw connecting line on windgust scattered plot
    const range = chartObj.selectAll('.series')
      .data(() => chartData.filter((d) => (d.key !== ChartConst.ChartLabelSpecs.wgs.key)))
      .enter().append('g')
      .attr('class', (d) => `series-${d.sequence}`);
    range.append('path')
      .attr('class', (d) => `line data-line-meteo${d.area
        ? ' line-area crosswind-line'
        : ' widspeed-line'}`)
      .attr('d', (d) => line(d.values))
      .style('stroke', (d) => (d.area
        ? '#crosswind-gradient'
        : '#windspeed-gradient'));
    range.append('path')
      .attr('class', ' crosswind-area')
      .style('stroke', (d) => (d.area
        ? '#crosswind-gradient'
        : d.color))
      .attr('d', (d) => {
        if (d.area) {
          // Draw area only if area flag is true for a given series
          return area(d.values);
        }
        return '';
      });
    chartObj.append('line')
      .classed('y-line-right', true)
      .attr('x1', width)
      .attr('y1', 0)
      .attr('x2', width)
      .attr('y2', chartHeight);

    // Draw Wind Gust for WGS
    if (gustData) {
      range.append('g')
        .attr('id', 'wind-gust')
        .selectAll('scatter-dots')
        .data((...args) => {
          // debugger
          const i = args[1];
          /**
           * adding this i === 0
           * because range can have multiple series. this will prevent from drawing multiple gust
           */
          return gustData.values.filter(() => (i === 0));
        })
        .enter()
        .append('rect')
        .attr('class', 'crispedges')
        .attr('stroke', ChartConst.ChartLabelSpecs.wgs.color)
        .attr('fill', ChartConst.ChartLabelSpecs.wgs.color)
        .attr('x', (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(gustData.values, x, d, 'x', key);
          const xValue = (positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2));
          return xValue;
        })
        .attr('y', (d) => {
          const yValue = y1(d.y) - (gustHeight / 2);
          return yValue;
        })
        .attr('width', (d, key) => {
          if (d.y === null) {
            // if gust is null then dont draw it i.e. width 0
            return 0;
          }
          const positionObj = bnd3Service.getNextCurrentPrevX(gustData.values, x, d, 'x', key);
          return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
            - positionObj.currentX) / 2);
        })
        .attr('height', gustHeight);
    }
    // Drawing wind barbs
    const barbStrip = range.append('g')
      .attr('class', 'barb-strip');
    barbStrip.selectAll('path')
      // Filter values where WSP is more that zero and have key Z i.e. a wdr
      .data((d) => d.values.filter((value) => (Object.prototype.hasOwnProperty.call(
        value,
        'z',
      ) && (value
        .y > 0))))
      .enter()
      .append('path')
      .attr('marker-start', ConfigApplicationSettings.settings.showBarbHead
        ? 'url(#meteo-barbheadLight)'
        : '')
      .attr('stroke', defaultWindSpeed.color) // Using windSpeen.light for configuring barbs
      .attr('stroke-width', defaultWindSpeed
        .width) // Using windSpeen.light for configuring barbs
      .attr('class', (d) => {
        const barbData = windbarbService.getWindBarbConfig(d.y, d.z, defaultWindSpeed
          .arrowWidth);
        return barbData['barb-class'];
      })
      .attr('d', (d) => {
        const barbData = windbarbService.getWindBarbConfig(d.y, d.z, defaultWindSpeed
          .arrowWidth);
        return barbData.d;
      })
      .attr('transform', (d) => {
        const barbData = windbarbService.getWindBarbConfig(d.y, d.z, defaultWindSpeed
          .arrowWidth);
        return `translate(${x(d.x)}, ${y1(d.y)}) scale(${barbData.transform.scale}), rotate(${barbData.transform.rotate}, 0, 0), translate(${barbData.transform.translate.x}, ${barbData.transform.translate.y})`;
      });
    // Drawing Calm wind clircles
    // Draw circles if wsp is zero
    const calmWind = barbStrip.append('g')
      .attr('class', 'barb-circles')
      .selectAll('path')
      .data((d) => d.values.filter((value) => (Object.prototype.hasOwnProperty.call(
        value,
        'z',
      ) && (d.y
          === 0))))
      .enter();
    calmWind.append('circle')
      .attr('r', ChartConst.windSpeed.calm.radius)
      .attr('stroke', ChartConst.windSpeed.calm.color)
      .attr('stroke-width', ChartConst.windSpeed.calm.strokeWidth)
      .attr('transform', (d) => `translate(${x(d.x)}, ${y1(d.y)})`);
    calmWind.append('circle')
      .attr('r', ChartConst.windSpeed.calm.radius * 2)
      .attr('fill', 'none')
      .attr('stroke', ChartConst.windSpeed.calm.color)
      .attr('stroke-width', ChartConst.windSpeed.calm.strokeWidth)
      .attr('transform', (d) => `translate(${x(d.x)}, ${y1(d.y)})`);
    // Drawing Y-axis left (Temperature)
    chartObj.append('g')
      .attr('class', 'y axis y-left')
      .call(yAxisLeft);

    return chartData;
  };
  return content;
}
export default windChartService();
